import React from 'react';
import dayjs from 'dayjs';
import {
  Blog as BlogType,
  UploadFile,
  Testimony,
  BlogBlogDynamicDynamicZone,
  Author,
  Event,
} from '../../../types/schema';
import { AllBlogContainer } from '../AllBlogPage/styles';
import PostItem from '../../ui/PostItem';
import { Author as AuthorStyles, ImageWrapper, PublishedWrapper, Wrapper } from './styles';
import Typography from '../../ui/Typography';
import DynamicZones from '../DynamicZones';
import HTMLParsedContent from '../../ui/HTMLParsedContent';
import { formatSrcSet } from '../../../utils/formatURLs';
import { FaInstagram, FaLinkedin, FaTwitter } from 'react-icons/fa';
import ClientComponent from '../../ui/ClientComponent/ClientComponent';

export type BlogPostTemplateProps = {
  language: string;
  title: string;
  relatedPosts: BlogType[];
  postAuthor: Author;
  publicationDate: string;
  featuredImage?: UploadFile;
  testimonies?: Testimony[];
  blogDynamic: BlogBlogDynamicDynamicZone[];
  events: Event[];
};

const BlogPostTemplate: React.FC<BlogPostTemplateProps> = ({
  featuredImage,
  language,
  events,
  title,
  postAuthor,
  publicationDate,
  relatedPosts,
  testimonies,
  blogDynamic,
}) => {
  const isEn = language === 'en';
  return (
    <Wrapper>
      {featuredImage && (
        <ImageWrapper>
          <img srcSet={formatSrcSet(featuredImage, 'postBanner')} alt={title} />
        </ImageWrapper>
      )}
      <PublishedWrapper style={{ maxWidth: '1200px', width: '100%' }}>
        <Typography variant="h1">{title}</Typography>
        <ClientComponent>
          <span>
            {isEn
              ? `Posted on ${dayjs(publicationDate).locale('en').format('DD MMMM YYYY')}`
              : `Publicado em ${dayjs(publicationDate).locale('pt-br').format('DD MMMM YYYY')}`}
          </span>
        </ClientComponent>
      </PublishedWrapper>
      <DynamicZones
        contentStyle={{ paddingTop: 10, paddingBottom: 10 }}
        dynamicZones={blogDynamic}
        allTestimonies={testimonies}
        allEvents={events}
        page="blog"
      />
      {postAuthor && (
        <AuthorStyles>
          <div>
            <img
              srcSet={formatSrcSet(
                (postAuthor.image as unknown as { formats?: { thumbnail?: string | UploadFile } })
                  ?.formats?.thumbnail ?? '',
                'technology',
              )}
              alt={isEn ? `Author ${postAuthor.name} photo` : `Foto do autor ${postAuthor.name}`}
              loading="lazy"
            />
          </div>

          <div>
            <h3>{postAuthor.name}</h3>
            <HTMLParsedContent content={postAuthor.body as string} />
            <div
              className="social-media"
              hidden={!postAuthor.linkedinUrl && !postAuthor.instagramUrl && !postAuthor.twitterUrl}
            >
              {postAuthor.linkedinUrl && (
                <a href={postAuthor.linkedinUrl} target="_blank" rel="noreferrer">
                  <FaLinkedin />
                </a>
              )}
              {postAuthor.instagramUrl && (
                <a href={postAuthor.instagramUrl} target="_blank" rel="noreferrer">
                  <FaInstagram />
                </a>
              )}
              {postAuthor.twitterUrl && (
                <a href={postAuthor.twitterUrl} target="_blank" rel="noreferrer">
                  <FaTwitter />
                </a>
              )}
            </div>
          </div>
        </AuthorStyles>
      )}
      <h2
        style={{
          maxWidth: '1200px',
          width: '100%',
          fontSize: '32px',
          margin: '40px auto',
          paddingTop: '40px',
          textAlign: 'center',
          color: '#1F2F59',
        }}
      >
        {isEn ? 'Related posts' : 'Posts relacionados'}
      </h2>
      <AllBlogContainer
        style={{ maxWidth: '1200px', width: '100%', margin: 'auto', paddingBottom: '40px' }}
        related
      >
        {relatedPosts.length > 0 ? (
          <ul>
            {relatedPosts.map((post) => (
              <PostItem related key={post.slug} post={post} isEn={isEn} />
            ))}
          </ul>
        ) : (
          <Typography variant="p" style={{ margin: 8 }}>
            {isEn
              ? `We couldn't find any posts related to this one`
              : 'Não encontramos nenhum post relacionado a este'}
          </Typography>
        )}
      </AllBlogContainer>
    </Wrapper>
  );
};

export default BlogPostTemplate;
